import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface City {
  name: string;
  x: number;
  y: number;
  isHome?: boolean;
  labelDx?: number;
  labelDy?: number;
  anchor?: 'start' | 'end';
}

// Coordinates converted from real lat/lng to viewBox 0 0 800 500
// Formula: x = (lng - 12.0) * 114.29, y = (51.1 - lat) * 192.31
const cities: City[] = [
  { name: 'Třebíč', x: 453, y: 359, isHome: true, labelDx: 12, labelDy: -8 },
  { name: 'Praha', x: 294, y: 215, labelDx: 10, labelDy: -6 },
  { name: 'Kladno', x: 261, y: 205, labelDx: -10, labelDy: -8, anchor: 'end' },
  { name: 'Hodonín', x: 579, y: 419, labelDx: -10, labelDy: 4, anchor: 'end' },
  { name: 'Mladá Boleslav', x: 346, y: 160, labelDx: 10, labelDy: -6 },
  { name: 'Žďár n. S.', x: 459, y: 301, labelDx: 10, labelDy: -6 },
  { name: 'Hradec Králové', x: 448, y: 193, labelDx: 10, labelDy: -6 },
  { name: 'Vimperk', x: 227, y: 383, labelDx: 10, labelDy: -6 },
  { name: 'Písek', x: 266, y: 342, labelDx: 10, labelDy: -6 },
  { name: 'Třinec', x: 740, y: 281, labelDx: -10, labelDy: -6, anchor: 'end' },
  { name: 'Bílina', x: 227, y: 137, labelDx: -10, labelDy: -2, anchor: 'end' },
  { name: 'Teplice', x: 231, y: 122, labelDx: 10, labelDy: -6 },
  { name: 'Sokolov', x: 107, y: 198, labelDx: 10, labelDy: -6 },
  { name: 'Kouty', x: 551, y: 233, labelDx: 10, labelDy: -6 },
];

// Czech Republic border from GeoJSON coordinates
// Formula: x = (lng - 12.0) * 105 + 40, y = (51.2 - lat) * 165 + 30
const czBorderPath = `
M 561,459 L 513,428 L 463,438 L 381,386 L 345,400 L 286,466 L 208,413
L 148,342 L 95,302 L 84,233 L 65,183 L 142,149 L 181,108 L 256,75
L 282,43 L 310,63 L 357,45 L 407,99 L 485,113 L 479,159 L 536,192
L 551,150 L 623,169 L 633,220 L 711,230 L 759,311 L 728,311 L 712,340
L 688,348 L 681,386 L 661,393 L 659,410 L 623,426 L 576,423 L 561,459 Z
`;

const CzechMap = () => {
  const { lang } = useLanguage();
  const homeLabel = lang === 'cs' ? 'Domovské město' : 'Home base';

  return (
    <TooltipProvider delayDuration={0}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-3xl mx-auto"
      >
        <svg
          viewBox="0 0 800 500"
          className="w-full h-auto"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Czech Republic outline */}
          <motion.path
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
            d={czBorderPath}
            fill="hsl(var(--muted) / 0.3)"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
            strokeLinejoin="round"
          />

          {/* City dots */}
          {cities.map((city, i) => (
            <Tooltip key={city.name}>
              <TooltipTrigger asChild>
                <motion.g
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.8 + i * 0.06, duration: 0.3 }}
                  style={{ cursor: 'pointer' }}
                >
                  {city.isHome && (
                    <motion.circle
                      cx={city.x}
                      cy={city.y}
                      r={14}
                      fill="hsl(var(--primary) / 0.15)"
                      animate={{ r: [14, 20, 14] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  )}
                  <circle
                    cx={city.x}
                    cy={city.y}
                    r={city.isHome ? 7 : 5}
                    fill="hsl(var(--primary))"
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                    style={city.isHome ? { filter: 'drop-shadow(0 0 6px hsl(var(--primary) / 0.6))' } : {}}
                  />
                  <text
                    x={city.x + (city.labelDx ?? 10)}
                    y={city.y + (city.labelDy ?? 4)}
                    fontSize={city.isHome ? 12 : 10}
                    fontWeight={city.isHome ? 700 : 400}
                    fill="hsl(var(--foreground))"
                    textAnchor={city.anchor ?? 'start'}
                    style={{ pointerEvents: 'none' }}
                  >
                    {city.name}
                  </text>
                </motion.g>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">{city.name}</p>
                {city.isHome && (
                  <p className="text-xs text-muted-foreground">{homeLabel}</p>
                )}
              </TooltipContent>
            </Tooltip>
          ))}
        </svg>
      </motion.div>
    </TooltipProvider>
  );
};

export default CzechMap;
